import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useClinicId } from "@/hooks/useClinicId";
import radNotificationsSocketService, {
  RAD_ORDER_NOTIFIED_EVENT,
} from "@/services/rad-notifications-socket.service";
import type { AppNotification } from "./types";

const MAX_NOTIFICATIONS = 100;

const sortByNewest = (items: AppNotification[]) =>
  [...items].sort((a, b) => dayjs(b.timestamp).valueOf() - dayjs(a.timestamp).valueOf());

function isValidRadNotification(payload: unknown): payload is AppNotification {
  if (!payload || typeof payload !== "object") return false;
  const item = payload as Partial<AppNotification>;
  return Boolean(item.eventId && item.accessionNumber && item.timestamp);
}

/**
 * Live RAD clinic notifications via Socket.IO `/rad-notifications`.
 * Listens for `order:notified` when authenticated with a selected clinic.
 */
export function useRadNotifications() {
  const { isAuthenticated } = useAuth();
  const clinicId = useClinicId();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.isRead).length,
    [notifications],
  );

  const markAsRead = useCallback((eventId: string) => {
    setNotifications((prev) =>
      prev.map((item) => (item.eventId === eventId ? { ...item, isRead: true } : item)),
    );
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !clinicId) {
      radNotificationsSocketService.disconnect();
      setNotifications([]);
      return;
    }

    // Clinic-scoped feed — reset when switching clinics.
    setNotifications([]);

    const handleOrderNotified = (data: unknown) => {
      if (!isValidRadNotification(data)) {
        console.warn("RAD notifications: ignored invalid order:notified payload", data);
        return;
      }

      setNotifications((prev) => {
        if (prev.some((item) => item.eventId === data.eventId)) {
          return prev;
        }
        const next: AppNotification = { ...data, isRead: false };
        return sortByNewest([next, ...prev]).slice(0, MAX_NOTIFICATIONS);
      });
    };

    const handleError = (data: unknown) => {
      console.error("RAD notifications socket error:", data);
    };

    radNotificationsSocketService.on(RAD_ORDER_NOTIFIED_EVENT, handleOrderNotified);
    radNotificationsSocketService.on("error", handleError);
    radNotificationsSocketService.connect(clinicId);

    return () => {
      radNotificationsSocketService.off(RAD_ORDER_NOTIFIED_EVENT, handleOrderNotified);
      radNotificationsSocketService.off("error", handleError);
      radNotificationsSocketService.disconnect();
    };
  }, [isAuthenticated, clinicId]);

  return {
    notifications,
    unreadCount,
    markAsRead,
  };
}
